import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import Project from "./project";
import SubworkProject from "./subworkProject";
import { useTheme } from "../../contexts/ThemeContext";
import { useUserInfo } from "../../hooks/useUserInfo";
import { Theme } from "../../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const Container = styled.div``;

const ProjectTitle = styled.h2<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.sectionHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.sectionHeading.fontWeight};
	line-height: ${props => props.theme.typography.roles.sectionHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.sectionHeading.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	margin: 2rem 0 0.875rem;
	position: relative;
	display: inline-block;
	animation: ${fadeInUp} 0.3s ease-out;

	&::after {
		content: '';
		position: absolute;
		bottom: -0.25rem;
		left: 0;
		width: 2rem;
		height: 2px;
		background: ${props => props.theme.colors.primary.main};
		border-radius: 2px;
	}

	&:first-of-type {
		margin-top: 0;
	}

	@media (max-width: 768px) {
		font-size: 1.5rem;
	}
`;

const ProjectSubtitle = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.body.fontSize};
	color: ${props => props.theme.colors.text.secondary};
	margin: 0 0 1rem;
	line-height: ${props => props.theme.typography.roles.body.lineHeight};
	white-space: pre-wrap;
	animation: ${fadeInUp} 0.6s ease-out 0.1s both;

	@media (max-width: 768px) {
		font-size: 1rem;
	}
`;

const ProjectsGrid = styled.div<{ theme: Theme }>`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: ${props => props.theme.density.gridGap};
	margin-bottom: 1rem;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
		gap: ${props => props.theme.density.gridGap};
	}
`;

const SubworkContainer = styled.div<{ theme: Theme }>`
	margin-top: 0.75rem;
	display: flex;
	flex-direction: column;
	gap: ${props => props.theme.density.gridGap};
`;

const AllProjects = () => {
	const { theme } = useTheme();
	const INFO = useUserInfo();

	return (
		<Container>
			<ProjectTitle theme={theme}>{INFO.projects.tools.title}</ProjectTitle>
			<ProjectsGrid theme={theme}>
				{INFO.projects.tools.items.map((project, index) => (
					<div key={index}>
						<Project
							logo={project.logo}
							title={project.title}
							description={project.description}
							linkText={project.linkText}
							link={project.link}
						/>
					</div>
				))}
			</ProjectsGrid>

			<ProjectTitle theme={theme}>{INFO.projects.app.title}</ProjectTitle>
			<ProjectSubtitle theme={theme}>{INFO.projects.app.description}</ProjectSubtitle>
			<ProjectsGrid theme={theme}>
				{INFO.projects.app.items.map((project, index) => (
					<div key={index}>
						<Project
							logo={project.logo}
							title={project.title}
							description={project.description}
							linkText={project.linkText}
							link={project.link}
							compact
						/>
					</div>
				))}
			</ProjectsGrid>

			<ProjectTitle theme={theme}>{INFO.projects.subwork.title}</ProjectTitle>
			<SubworkContainer theme={theme}>
				{INFO.projects.subwork.items.map((project, index) => (
					<div key={index}>
						<SubworkProject
							logo={project.logo}
							terms={project.terms}
							title={project.title}
							description={project.description}
							linkText={project.linkText}
							link={project.link}
						/>
					</div>
				))}
			</SubworkContainer>
		</Container>
	);
};

export default AllProjects;
